import React, { useEffect, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import {
  BsCheckCircleFill,
  BsXCircleFill,
  BsPersonCheckFill,
  BsPersonXFill,
  BsClockHistory,
} from "react-icons/bs";
import "./SignUpTrainee.css";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Swal from "sweetalert2";
import riuLogo from "../../img/Riu_TM_Logo.png";

// ── Constants ────────────────────────────────────────────────────────────────
const companyOptions = [
  { label: "Kennametal India Limited (KIL)", value: "Kennametal India Limited (KIL)" },
  { label: "Kennametal Shared Services Private Limited (KSSPL)", value: "Kennametal Shared Services Private Limited (KSSPL)" },
  { label: "Others, please specify", value: "Others, please specify" },
];

const AudienceOptions = [
  { label: "Permanent Employee", value: "EMPLOYEES_PERMANENT" },
  { label: "Other than Permanent Employee", value: "EMPLOYEES_TEMPORARY" },
  { label: "Permanent Worker", value: "WORKERS_PERMANENT" },
  { label: "Other than Permanent Worker", value: "WORKERS_TEMPORARY" },
  { label: "KMP", value: "KMP" },
  { label: "BOD", value: "BOD" },
  { label: "Customer", value: "CUSTOMERS" },
  { label: "Supplier", value: "SUPPLIERS" },
  { label: "Distributor", value: "DISTRIBUTORS" },
];

// ── Phase constants ───────────────────────────────────────────────────────────
const PHASE = {
  LOOKUP: "lookup",       // initial — only Employee ID field + Check button
  FOUND: "found",         // employee found — show user card + action button
  NOT_FOUND: "not_found", // employee not in system — show extra fields + ATTENDED button
  DONE: "done",           // attendance submitted — show success state
};

// ── Status badge helper ───────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    ATTENDED: { label: "Already Attended", color: "#16a34a", bg: "#dcfce7", Icon: BsPersonCheckFill },
    NOT_ATTENDED: { label: "Marked Not Attended", color: "#dc2626", bg: "#fee2e2", Icon: BsPersonXFill },
    NONE: { label: "No Attendance Marked Yet", color: "#d97706", bg: "#fef9c3", Icon: BsClockHistory },
  };
  const { label, color, bg, Icon } = map[status] || map.NONE;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px",
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 600,
        color,
        background: bg,
      }}
    >
      <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
      {label}
    </span>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
const ExternalTraineeAttendance = () => {
  const { token } = useParams();

  const [trainingData, setTrainingData] = useState({});
  const [trainingToken, setTrainingToken] = useState(null);

  // ── Step 1 state
  const [employeeId, setEmployeeId] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  // ── Phase + found-user state
  const [phase, setPhase] = useState(PHASE.LOOKUP);
  const [foundUser, setFoundUser] = useState(null); // { name, email, attendanceStatus }

  // ── Unregistered extra fields
  const [gender, setGender] = useState("MALE");
  const [companyName, setCompanyName] = useState("");
  const [otherCompany, setOtherCompany] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [categoryId, setCategoryId] = useState("");


  const [submitLoading, setSubmitLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Done state
  const [doneMessage, setDoneMessage] = useState("");
  const [doneStatus, setDoneStatus] = useState(""); // "ATTENDED" | "NON_ATTENDANT"

  // ── Load training info ────────────────────────────────────────────────────
  const callTraineeFunction = async (tkn) => {
    if (!tkn) return;
    const { isSuccess, data } = await apiCall(
      `${config.AUTH_API_URL_COMPANY}getTraineeData`,
      {},
      { token: tkn },
      "GET"
    );
    if (isSuccess) setTrainingData(data?.data || {});
  };

  useEffect(() => {
    const resultToken = token?.includes("&") ? token.split("&")[0] : token;
    if (resultToken) {
      setTrainingToken(resultToken);
      callTraineeFunction(resultToken);
    }
  }, []);

  // ── Step 1: Look up employee ──────────────────────────────────────────────
  const handleLookup = async () => {
    if (!employeeId.trim()) {
      setLookupError("Employee ID is required.");
      return;
    }
    // Always clear previous results before a new lookup
    setLookupError("");
    setFoundUser(null);
    setPhase(PHASE.LOOKUP);
    setFieldErrors({});
    setLookupLoading(true);

    const { isSuccess, data } = await apiCall(
      `${config.AUTH_API_URL_COMPANY}lookupEmployeeForAttendance`,
      {},
      { employeeId: employeeId.trim(), token: trainingToken || "direct" },
      "GET"
    );

    setLookupLoading(false);

    if (!isSuccess) {
      // Network / server error
      setLookupError(data?.message || "Something went wrong. Please try again.");
      return;
    }

    const payload = data?.data;

    if (payload?.found) {
      setFoundUser(payload);
      setPhase(PHASE.FOUND);
    } else {
      // Employee not in system → reveal unregistered fields
      setFoundUser(null);
      setPhase(PHASE.NOT_FOUND);
    }
  };

  // ── Step 2 validation (unregistered only) ────────────────────────────────
  const validateUnregistered = () => {
    const errs = {};
    if (!companyName) errs.companyName = "Company is required.";
    if (companyName === "Others, please specify" && !otherCompany.trim())
      errs.otherCompany = "Please specify the company name.";
    if (!categoryId) errs.categoryId = "Category is required.";
    if (!joiningDate) {
      errs.joiningDate = "Joining date is required.";
    } else {
      const d = new Date(joiningDate);
      if (isNaN(d.getTime())) errs.joiningDate = "Enter a valid date.";
      else if (d > new Date()) errs.joiningDate = "Joining date cannot be in the future.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Step 2: Mark attendance ───────────────────────────────────────────────
  const handleMark = async (trainingStatus) => {
    const isUnregistered = phase === PHASE.NOT_FOUND;
    if (isUnregistered && !validateUnregistered()) return;

    setSubmitLoading(true);

    const finalCompany =
      companyName === "Others, please specify" ? otherCompany : companyName;

    const payload = {
      employeeId: employeeId.trim(),
      token: trainingToken || "direct",
      trainingStatus,
      isUnregistered,
      ...(isUnregistered && {
        gender,
        companyName: finalCompany,
        joiningDate: joiningDate || null,
        categoryId,
      }),
    };

    const { isSuccess, data } = await apiCall(
      `${config.AUTH_API_URL_COMPANY}markExternalAttendanceByEmployeeId`,
      {},
      payload,
      "POST"
    );

    setSubmitLoading(false);

    if (isSuccess) {
      const msg = data?.message || "Your response has been recorded successfully.";
      setDoneMessage(msg);
      setDoneStatus(trainingStatus);
      setPhase(PHASE.DONE);
    } else {
      const errMsg = data?.message || "Something went wrong. Please try again.";
      Swal.fire({
        icon: "error",
        title: "Could not mark attendance",
        text: errMsg,
        confirmButtonColor: "#3F88A5",
      });
    }
  };

  // ── Determine which buttons to show based on attendanceStatus ────────────
  const getButtonConfig = (attendanceStatus) => {
    // Returns { showAttended: bool, showNotAttended: bool }
    if (attendanceStatus === "ATTENDED") {
      return { showAttended: false, showNotAttended: true };
    }
    if (attendanceStatus === "NOT_ATTENDED") {
      return { showAttended: true, showNotAttended: false };
    }
    // NONE — only ATTENDED enabled
    return { showAttended: true, showNotAttended: false };
  };

  // ── Reset to start ────────────────────────────────────────────────────────
  const resetAll = () => {
    setEmployeeId("");
    setLookupError("");
    setFoundUser(null);
    setPhase(PHASE.LOOKUP);
    setGender("MALE");
    setCompanyName("");
    setOtherCompany("");
    setJoiningDate("");
    setCategoryId("");
    setFieldErrors({});
    setDoneMessage("");
    setDoneStatus("");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="register-container">
      <div className="register-form">
        {/* Logo */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
          <img src={riuLogo} alt="RIU Logo" style={{ height: 80, objectFit: "contain" }} />
        </div>

        <h2 className="form-title">Mark Attendance</h2>

        {/* Training Details Panel */}
        <div
          className="p-4 bg-light rounded shadow-sm mb-4"
          style={{ borderLeft: "6px solid #3F88A5" }}
        >
          <Row className="mb-2">
            <Col sm="12">
              <Form.Label className="text-muted small mb-0">Training Title</Form.Label>
              <div className="fs-5 fw-bold" style={{ color: "#3F88A5" }}>
                {trainingData.trainingTitle || "—"}
              </div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col sm="12">
              <Form.Label className="text-muted small mb-0">Description</Form.Label>
              <div className="fs-6 text-dark">{trainingData.description || "—"}</div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col sm="6">
              <Form.Label className="text-muted small mb-0">Facilitator</Form.Label>
              <div className="fs-6 fw-medium text-dark">
                {trainingData.trainingFacilitator || "—"}
              </div>
            </Col>
            <Col sm="6">
              <Form.Label className="text-muted small mb-0">Trainers</Form.Label>
              <div className="fs-6 fw-medium text-dark">
                {trainingData.trainers?.map((t) => t.name).join(", ") || "N/A"}
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <Form.Label className="text-muted small mb-0">Date</Form.Label>
              <div className="fs-6 fw-medium text-dark">
                {trainingData.fromDate} – {trainingData.toDate}
              </div>
            </Col>
            <Col sm="6">
              <Form.Label className="text-muted small mb-0">Time</Form.Label>
              <div className="fs-6 fw-medium text-dark">
                {trainingData.fromTime} – {trainingData.toTime}
              </div>
            </Col>
          </Row>
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PHASE: DONE */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {phase === PHASE.DONE && (
          <div
            style={{
              background: doneStatus === "ATTENDANT" ? "#f0fdf4" : "#fff7ed",
              border: `2px solid ${doneStatus === "ATTENDANT" ? "#16a34a" : "#ea580c"}`,
              borderRadius: 12,
              padding: "24px 20px",
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            {doneStatus === "ATTENDANT" ? (
              <BsPersonCheckFill style={{ width: 40, height: 40 }} color="#16a34a" />
            ) : (
              <BsPersonXFill style={{ width: 40, height: 40 }} color="#ea580c" />
            )}
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginTop: 12,
                color: doneStatus === "ATTENDANT" ? "#16a34a" : "#ea580c",
              }}
            >
              {doneStatus === "ATTENDANT" ? "Attendance Marked!" : "Non-Attendance Marked!"}
            </div>
            <div style={{ color: "#555", marginTop: 6, fontSize: 14 }}>{doneMessage}</div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PHASE: LOOKUP / FOUND / NOT_FOUND                                 */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {phase !== PHASE.DONE && (
          <>
            {/* ── Employee ID field + Check button inline row ──────────── */}
            <Form.Group controlId="formEmployeeID" className="mb-2">
              <Form.Label>Employee ID *</Form.Label>
              <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                <Form.Control
                  type="text"
                  className="form-controll"
                  placeholder="Enter Employee ID"
                  value={employeeId}
                  style={{ flex: 1 }}
                  onChange={(e) => {
                    setEmployeeId(e.target.value);
                    setLookupError("");
                    // Clear any previously fetched result immediately
                    setFoundUser(null);
                    setPhase(PHASE.LOOKUP);
                    setFieldErrors({});
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                />
                <Button
                  variant="primary"
                  className="d-flex align-items-center justify-content-center gap-2"
                  style={{
                    background: "#3F88A5",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 22px",
                    fontWeight: 600,
                    fontSize: 15,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    height: "100%",
                  }}
                  disabled={lookupLoading}
                  onClick={handleLookup}
                >
                  <FiSearch style={{ width: 17, height: 17, flexShrink: 0 }} />
                  {lookupLoading ? "Checking…" : "Check Employee ID"}
                </Button>
              </div>
              {lookupError && (
                <p style={{ color: "red", fontSize: 13, fontWeight: "bold", marginTop: 4, userSelect: "none" }}>
                  {lookupError}
                </p>
              )}
            </Form.Group>



            {/* ══════════════════════════════════════════════════════════════ */}
            {/* PHASE: FOUND — user card + single action button               */}
            {/* ══════════════════════════════════════════════════════════════ */}
            {phase === PHASE.FOUND && foundUser && (() => {
              const { showAttended, showNotAttended } = getButtonConfig(foundUser.attendanceStatus);
              return (
                <>
                  {/* User card — avatar+name+status on left, action button on right */}
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      borderRadius: 10,
                      padding: "16px 18px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    {/* Left: avatar + name + status badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          background: "#3F88A5",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 20,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {(foundUser.name || employeeId)[0]?.toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {foundUser.name || employeeId}
                        </div>
                        {foundUser.email && (
                          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {foundUser.email}
                          </div>
                        )}
                        <StatusBadge status={foundUser.attendanceStatus} />
                      </div>
                    </div>

                    {/* Right: action button */}
                    <div style={{ flexShrink: 0 }}>
                      {showAttended && (
                        <button
                          disabled={submitLoading}
                          onClick={() => handleMark("ATTENDANT")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "9px 20px",
                            borderRadius: 8,
                            border: "2px solid #16a34a",
                            background: "transparent",
                            color: "#16a34a",
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: submitLoading ? "not-allowed" : "pointer",
                            opacity: submitLoading ? 0.65 : 1,
                            transition: "background 0.18s, color 0.18s",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#16a34a"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#16a34a"; }}
                        >
                          <BsCheckCircleFill style={{ width: 16, height: 16, flexShrink: 0 }} />
                          {submitLoading ? "Submitting…" : "Mark attended"}
                        </button>
                      )}
                      {showNotAttended && (
                        <button
                          disabled={submitLoading}
                          onClick={() => handleMark("NON_ATTENDANT")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "9px 20px",
                            borderRadius: 8,
                            border: "2px solid #dc2626",
                            background: "transparent",
                            color: "#dc2626",
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: submitLoading ? "not-allowed" : "pointer",
                            opacity: submitLoading ? 0.65 : 1,
                            transition: "background 0.18s, color 0.18s",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#dc2626"; }}
                        >
                          <BsXCircleFill style={{ width: 16, height: 16, flexShrink: 0 }} />
                          {submitLoading ? "Submitting…" : "Mark not attended"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Contextual info message */}
                  {foundUser.attendanceStatus === "ATTENDED" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 8,
                        padding: "12px 16px",
                        fontSize: 13,
                        color: "#1e40af",
                        marginBottom: 16,
                      }}
                    >
                      <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
                      Attendance already marked. You can switch to not attended using the button above.
                    </div>
                  )}
                  {foundUser.attendanceStatus === "NOT_ATTENDED" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 8,
                        padding: "12px 16px",
                        fontSize: 13,
                        color: "#9a3412",
                        marginBottom: 16,
                      }}
                    >
                      <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
                      Marked as not attended. You can switch to attended using the button above.
                    </div>
                  )}
                  {foundUser.attendanceStatus === "NONE" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fefce8",
                        border: "1px solid #fde68a",
                        borderRadius: 8,
                        padding: "12px 16px",
                        fontSize: 13,
                        color: "#78350f",
                        marginBottom: 16,
                      }}
                    >
                      <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
                      No attendance marked yet. Click "Mark attended" to confirm presence.
                    </div>
                  )}
                </>
              );
            })()}

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* PHASE: NOT_FOUND — extra fields + ATTENDED only               */}
            {/* ══════════════════════════════════════════════════════════════ */}
            {phase === PHASE.NOT_FOUND && (
              <>
                {/* Info banner */}
                <div
                  style={{
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#c2410c",
                    marginBottom: 18,
                  }}
                >
                  <strong>Employee ID not found.</strong> Please fill in your basic details below.
                  A system account will be created for you automatically.
                </div>

                {/* Gender */}
                <Form.Group controlId="formGender" className="mb-3">
                  <Form.Label>Gender *</Form.Label>
                  <div className="select-wrapper">
                    <Form.Control
                      as="select"
                      className="form-controll"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                      <option value="OTHER">Other</option>
                    </Form.Control>
                    <FiChevronDown style={{ width: 16, height: 16, flexShrink: 0 }} className="dropdown-icon" />
                  </div>
                </Form.Group>

                {/* Company */}
                <Form.Group controlId="formCompany" className="mb-2">
                  <Form.Label>Company *</Form.Label>
                  <div className="select-wrapper">
                    <Form.Control
                      as="select"
                      className="form-controll"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    >
                      <option value="">Select Company</option>
                      {companyOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Control>
                    <FiChevronDown style={{ width: 16, height: 16, flexShrink: 0 }} className="dropdown-icon" />
                  </div>
                  {fieldErrors.companyName && (
                    <p style={{ color: "red", fontSize: 13, fontWeight: "bold", marginTop: 4, userSelect: "none" }}>
                      {fieldErrors.companyName}
                    </p>
                  )}
                </Form.Group>

                {companyName === "Others, please specify" && (
                  <Form.Group controlId="formOtherCompany" className="mb-3">
                    <Form.Label>Specify Company Name</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-controll"
                      placeholder="Enter company name"
                      value={otherCompany}
                      onChange={(e) => setOtherCompany(e.target.value)}
                    />
                    {fieldErrors.otherCompany && (
                      <p style={{ color: "red", fontSize: 13, fontWeight: "bold", marginTop: 4, userSelect: "none" }}>
                        {fieldErrors.otherCompany}
                      </p>
                    )}
                  </Form.Group>
                )}

                {/* Joining Date */}
                <Form.Group controlId="formJoiningDate" className="mb-3">
                  <Form.Label>Joining Date *</Form.Label>
                  <Form.Control
                    type="date"
                    className="form-controll"
                    value={joiningDate}
                    onChange={(e) => setJoiningDate(e.target.value)}
                  />
                  {fieldErrors.joiningDate && (
                    <p style={{ color: "red", fontSize: 13, fontWeight: "bold", marginTop: 4, userSelect: "none" }}>
                      {fieldErrors.joiningDate}
                    </p>
                  )}
                </Form.Group>

                {/* Category */}
                <Form.Group controlId="formCategory" className="mb-4">
                  <Form.Label>Category *</Form.Label>
                  <div className="select-wrapper">
                    <Form.Control
                      as="select"
                      className="form-controll"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                    >
                      <option value="">Select category</option>
                      {AudienceOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Control>
                    <FiChevronDown style={{ width: 16, height: 16, flexShrink: 0 }} className="dropdown-icon" />
                  </div>
                  {fieldErrors.categoryId && (
                    <p style={{ color: "red", fontSize: 13, fontWeight: "bold", marginTop: 4, userSelect: "none" }}>
                      {fieldErrors.categoryId}
                    </p>
                  )}
                </Form.Group>

                {/* Only ATTENDED for unregistered users */}
                <Button
                  variant="success"
                  className="register-btn w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={submitLoading}
                  onClick={() => handleMark("ATTENDANT")}
                >
                  <BsCheckCircleFill style={{ width: 16, height: 16, flexShrink: 0 }} />
                  {submitLoading ? "Submitting…" : "ATTENDED"}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalTraineeAttendance;